import moment from 'moment';
import { format, parse } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import type { Locale } from 'date-fns';
import { DATE_FORMAT } from 'utils';
import { Session, Event } from 'types/listings';
import { SessionStatus } from '@kouto/types';

interface IOption {
  startTime: string;
  duration: string;
  dateTimeFormat?: string;
}

export function getSessionStartEndTime({
  startTime,
  duration,
  dateTimeFormat = 'h:mm a',
}: IOption) {
  const startDateTime = moment(startTime);
  const durationInMinutes = moment.duration(duration, 'minutes').asMinutes();
  const endDateTime = startDateTime.clone().add(durationInMinutes, 'minutes');

  return {
    startsAt: startDateTime.format(dateTimeFormat),
    endsAt: endDateTime.format(dateTimeFormat),
    durationInMinutes,
  };
}

/**
 * Whether a session row is one a guest can start a booking in.
 *
 * A scheduled session always is; an open span of a continuously booked resource only when it is long
 * enough for the group's shortest booking, which the API marks on the span.
 */
export const isBookableSessionRow = (session: {
  isBookableAvailability?: boolean;
  isContinuousAvailability?: boolean;
}): boolean =>
  !session.isContinuousAvailability || session.isBookableAvailability !== false;

/**
 * Whether a scheduled session can still take a booking. A pool-backed resource held through another
 * group comes back with its capacity capped to its participant count, so it reads as unbooked yet full.
 * A null capacity is an open experience, which has no limit.
 */
export const hasRemainingCapacity = (session: {
  capacity: number | null;
  participantCount: number;
}): boolean =>
  session.capacity === null || session.capacity - session.participantCount > 0;

export const getDatesFromSessions = (sessions: Session[]) => {
  const today = moment().format(DATE_FORMAT);
  return sessions
    .filter(isBookableSessionRow)
    .map((session) => session.startDateTime.split('T')[0])
    .filter((date) => !!date)
    .filter((date, index, self) => self.indexOf(date) === index)
    .filter((date) => date >= today)
    .sort((d1, d2) => (d1 > d2 ? 1 : -1));
};

export const getTimesFromSessions = (sessions: Session[]) => {
  return sessions
    .map((session) => session.startDateTime.split('T')[1])
    .filter((date, index, self) => self.indexOf(date) === index)
    .sort((time1, time2) => (time1 > time2 ? 1 : -1))
    .map((time) => ({
      value: time,
      label: moment(time, 'HH:mm:ss').format('LT'),
    }));
};

const formatTime = (time: string, locale: Locale) =>
  format(parse(time, 'HH:mm:ss', new Date()), 'p', { locale });

export const getFormattedTimes = (
  startTimes?: Event['startTimes'],
  locale: Locale = enUS,
) => {
  const times = startTimes?.sort((a, b) => (a > b ? 1 : -1)) || [];

  if (times.length === 0) {
    return '';
  }

  if (times.length === 1) {
    return formatTime(times[0], locale);
  }

  return `${formatTime(times[0], locale)} - ${formatTime(
    times[times.length - 1],
    locale,
  )}`;
};

export const filterSessionsByDate = (
  sessions: Session[],
  date: string,
): Session[] =>
  sessions.filter((session) => session.startDateTime.split('T')[0] === date);

export const getDateTimesLabelFromSessionsAvailability = (
  sessions: Session[],
  date: string,
  waitlistActive: boolean,
  locale?: Locale,
) => {
  const sessionsOnDate = filterSessionsByDate(sessions, date);

  const availableSessionsOnDate = waitlistActive
    ? sessionsOnDate
    : filterAvailableSessions(sessionsOnDate);

  const availableTimesOnDate = Array.from(
    new Set(
      availableSessionsOnDate.map(
        (session) => session.startDateTime.split('T')[1],
      ),
    ),
  );

  return getFormattedTimes(availableTimesOnDate, locale);
};

export const areSessionsFree = (sessions: Session[], date: string): boolean => {
  const sessionsOnDate = filterSessionsByDate(sessions, date);

  return sessionsOnDate.every((session) =>
    session.priceTiers.every((tier) => tier.price === 0),
  );
};

export const filterAvailableSessions = (sessions: Session[]) => {
  return filterNonBlockedSessions(
    sessions.filter(
      (session) =>
        session.participantCount === 0 ||
        (session.participantCount < (session.capacity || 0) &&
          !session.isPrivateEvent),
    ), // non booked sessions
  );
};

export const filterNonBlockedSessions = (sessions: Session[]) => {
  // TODO filter out sessions that are in booking cutoff
  return sessions.filter(
    (session) => session.status !== SessionStatus.OVERLAPPED,
  );
};
